import { useCart } from '@/hooks/useCart';
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useEmailTypoCheck } from '@/hooks/useEmailTypoCheck';
import { Lock, Shield, Headphones, CreditCard, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import CheckoutHeader from '@/components/CheckoutHeader';
import { useAuth } from '@/hooks/useAuth';
import { useTranslations } from '@/hooks/useTranslations';
import { useLanguage } from '@/hooks/useLanguage';
import twintLogo from '@/assets/twint-logo.webp';
// Stripe.js (~220 KB) is heavy and only needed when the user actually selects
// the card/Stripe option. Lazy-load it so it does NOT block the checkout
// (or any other) page on first paint. TWINT is the default payment method,
// so most users never download Stripe at all.
const StripePaymentForm = lazy(() => import('@/components/StripePaymentForm'));
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { useOmnisendTracking } from '@/hooks/useOmnisendTracking';
import CheckoutBumps, { SelectedBump } from '@/components/CheckoutBumps';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { usePageTitle } from '@/hooks/usePageTitle';
import LoyaltyRedeemBox from '@/components/loyalty/LoyaltyRedeemBox';
import PointsBadge from '@/components/loyalty/PointsBadge';
import { useMyLoyalty, pointsToChf } from '@/hooks/useLoyalty';
import { detectStorefront } from '@/hooks/useStorefront';
import { useStorefront } from '@/hooks/useStorefront';
import { useStorefrontVat } from '@/hooks/useStorefrontVat';
import { formatPrice, formatPriceForCurrency } from '@/lib/formatPrice';
import { buildOrderAmounts } from '@/lib/buildOrderAmounts';
import { useSessionExchangeRate } from '@/hooks/useSessionExchangeRate';
import { type PaymentProvider, defaultPaymentMethod, paymentMethodsForStorefront, paymentMethodsLabel } from '@/lib/paymentMethods';
import { useDisplayPriceSum } from '@/hooks/useDisplayPrice';

// Track checkout_start event
const trackCheckoutStart = () => {
  void trackAnalyticsEvent('checkout_start');
};

function CheckoutLayout({ children, step = 1 }: { children: React.ReactNode; step?: number }) {
  const { t } = useTranslations();
  usePageTitle('Checkout');
  const { localePath } = useLanguage();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <CheckoutHeader step={step} />
      <main className="flex-1">{children}</main>
      <footer className="border-t py-3 md:py-4">
        <div className="container flex flex-wrap items-center justify-center gap-2 md:gap-4 text-[10px] md:text-xs text-muted-foreground">
          <Link to={localePath('/agb')} className="hover:text-foreground hover:underline">{t('footer.terms')}</Link>
          <span>·</span>
          <Link to={localePath('/datenschutz')} className="hover:text-foreground hover:underline">{t('footer.privacy')}</Link>
          <span>·</span>
          <Link to={localePath('/impressum')} className="hover:text-foreground hover:underline">{t('footer.imprint')}</Link>
        </div>
      </footer>
    </div>
  );
}

export default function CheckoutPage() {
  const { t } = useTranslations();
  const { locale, localePath } = useLanguage();
  const { user } = useAuth();
  const { items, total, clearCart, discountThreshold, discountPercent } = useCart();
  const { rate: sessionExchangeRate } = useSessionExchangeRate();
  // P2.6 §4b (Blocker 1): SSOT for the *displayed and charged* line/subtotal
  // amounts. Uses the same override + FX + .90-rounding path as home/PDP.
  // CH fastpath: hook returns unchanged CHF numbers, byte-identical.
  const sf = useStorefront();
  const cartDisplay = useDisplayPriceSum(items.map(i => ({ chf: i.price, variationId: i.variationId })));
  const displayCurrency = sf.currency;
  const fmt = (v: number) => formatPriceForCurrency(v, displayCurrency);
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';
  const orderNumber = searchParams.get('order');
  const retryOrderId = searchParams.get('retry');
  const [step, setStep] = useState(isSuccess ? 2 : 1);
  const { trackCheckoutStarted, trackContactIdentified, trackCheckoutStep, trackCheckoutField } = useOmnisendTracking();

  // Track checkout start (internal analytics + Omnisend)
  useEffect(() => {
    if (!isSuccess && items.length > 0) {
      trackCheckoutStart();
      trackCheckoutStarted(items, total, user?.email || undefined);
    }
  }, [isSuccess, items, total, user?.email, trackCheckoutStarted]);

  // Retry order state
  const [retryOrder, setRetryOrder] = useState<{
    id: string;
    order_number: number;
    email: string;
    total: number;
    subtotal: number;
    discount: number;
    vat_amount: number;
    discount_code: string | null;
    notes: string | null;
    country_code: string | null;
  } | null>(null);
  const [retryItems, setRetryItems] = useState<{ product_title: string; variation_label: string; price: number; quantity: number }[]>([]);
  const [retryLoading, setRetryLoading] = useState(!!retryOrderId);

  // Load saved guest checkout data from localStorage
  const savedCheckout = (() => {
    try {
      const raw = localStorage.getItem('bf_checkout_data');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  // Form state – prefill from localStorage for guests
  const [email, setEmail] = useState(savedCheckout?.email || '');
  const emailSuggestion = useEmailTypoCheck(email);
  const [firstName, setFirstName] = useState(savedCheckout?.firstName || '');
  const [lastName, setLastName] = useState(savedCheckout?.lastName || '');
  const [address, setAddress] = useState(savedCheckout?.address || '');
  const [postalCode, setPostalCode] = useState(savedCheckout?.postalCode || '');
  const [city, setCity] = useState(savedCheckout?.city || '');
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState<{ code: string; percent: number; amount: number } | null>(null);
  // Country is HARD-LOCKED to the active storefront (CH → 'CH', AT → 'AT').
  // The UI no longer exposes a picker; billing country is derived from the
  // storefront. MwSt continues to come exclusively from vat_settings via
  // useStorefrontVat — fixing the country here does NOT reactivate any
  // country-based VAT logic.
  const [countryCode, setCountryCode] = useState(sf.countryCode);
  useEffect(() => {
    if (countryCode !== sf.countryCode) setCountryCode(sf.countryCode);
  }, [sf.countryCode, countryCode]);
  const [selectedBumps, setSelectedBumps] = useState<SelectedBump[]>([]);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const { overview: loyaltyOverview, refresh: refreshLoyalty } = useMyLoyalty();

  // Persist checkout data to localStorage on change
  useEffect(() => {
    if (isSuccess) return;
    try {
      localStorage.setItem('bf_checkout_data', JSON.stringify({
        email, firstName, lastName, address, postalCode, city, countryCode,
      }));
    } catch { /* noop */ }
  }, [email, firstName, lastName, address, postalCode, city, countryCode, isSuccess]);

  // VAT state (dropdown list of active countries — display only).
  
  // SSOT: rate that is BOTH displayed and charged comes from useStorefrontVat.
  const { rate: storefrontVatRate, loading: vatLoading, error: vatError } = useStorefrontVat();

  // Payment state
  // P2.6 §3: Single Source of Truth for available methods is
  // PAYMENT_METHODS_BY_STOREFRONT. CH defaults to TWINT (index 0), all
  // other storefronts default to Stripe — byte-identical to §2.
  const [selectedPayment, setSelectedPayment] = useState<PaymentProvider>(
    defaultPaymentMethod(detectStorefront())
  );
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  // Load retry order data
  useEffect(() => {
    if (!retryOrderId) return;
    const loadRetryOrder = async () => {
      const [orderRes, itemsRes] = await Promise.all([
        supabase.from('orders')
          .select('id, order_number, email, total, subtotal, discount, vat_amount, discount_code, notes, country_code')
          .eq('id', retryOrderId)
          .in('status', ['pending', 'failed'])
          .single(),
        supabase.from('order_items')
          .select('product_title, variation_label, price, quantity')
          .eq('order_id', retryOrderId),
      ]);
      if (orderRes.data) {
        setRetryOrder(orderRes.data as any);
        setRetryItems(itemsRes.data || []);
        setEmail(orderRes.data.email);
        // Country stays locked to the active storefront — do not adopt the
        // retry order's country_code.
        if (orderRes.data.notes) {
          const cleanNotes = orderRes.data.notes.split('\n').filter((l: string) => !l.startsWith('[Bexio]')).join('\n').trim();
          const parts = cleanNotes.split(', ');
          if (parts.length >= 3) {
            const nameParts = parts[0].split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
            setAddress(parts[1] || '');
            const lastPart = parts[2] || '';
            const spaceIdx = lastPart.indexOf(' ');
            if (spaceIdx > 0) {
              setPostalCode(lastPart.substring(0, spaceIdx));
              setCity(lastPart.substring(spaceIdx + 1));
            }
          }
        }
      }
      setRetryLoading(false);
    };
    loadRetryOrder();
  }, [retryOrderId]);

  // Prefill from saved address & user email
  useEffect(() => {
    if (!user || retryOrderId) return;
    if (user.email && !email) setEmail(user.email);

    const prefillAddress = async () => {
      const { data } = await supabase
        .from('customer_addresses')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .limit(1)
        .single();
      if (data) {
        setFirstName(prev => prev || data.first_name);
        setLastName(prev => prev || data.last_name);
        setAddress(prev => prev || data.address);
        setPostalCode(prev => prev || data.postal_code);
        setCity(prev => prev || data.city);
        // Country stays locked to the active storefront.
      }
    };
    prefillAddress();
  }, [user]);


  const isRetry = !!retryOrder;
  // Bumps use finalPrice (already applied) — for CH this is CHF, non-CH we
  // must convert 1:1 via FX (bumps have no per-country override at present).
  const bumpTotalChf = selectedBumps.reduce((sum, b) => sum + b.finalPrice, 0);
  const bumpTotal = sf.storefront === 'ch'
    ? bumpTotalChf
    // Auto-convert; if rate not yet loaded fall back to 0 (payment gated below).
    : (cartDisplay.sum && sessionExchangeRate ? bumpTotalChf * sessionExchangeRate : bumpTotalChf);
  const bumpLineDisplay = (chf: number) =>
    sf.storefront === 'ch' ? chf : (sessionExchangeRate ? chf * sessionExchangeRate : chf);
  // Line prices for the summary column (per item), display-currency, override+FX aware.
  const lineDisplayPrices: number[] = cartDisplay.lines.length === items.length
    ? cartDisplay.lines
    : items.map(i => i.price); // fallback until sum resolves
  const itemsDisplaySubtotal = cartDisplay.sum?.amount ?? items.reduce((s, i) => s + i.price, 0);
  const subtotal = isRetry ? Number(retryOrder.subtotal) : itemsDisplaySubtotal + bumpTotal;
  const hasVolumeDiscount = isRetry ? Number(retryOrder.discount) > 0 : subtotal >= discountThreshold;
  const volumeDiscount = isRetry ? Number(retryOrder.discount) : (hasVolumeDiscount ? subtotal * (discountPercent / 100) : 0);
  const afterVolumeDiscount = subtotal - volumeDiscount;
  const codeDiscount = isRetry ? 0 : (discountApplied ? (afterVolumeDiscount * (discountApplied.percent / 100) + discountApplied.amount) : 0);
  const loyaltyDiscount = isRetry ? 0 : pointsToChf(redeemPoints, loyaltyOverview?.settings ?? null);
  // P2.6 §5 (revised): VAT rate is resolved storefront-first from vat_settings
  // via useStorefrontVat. No hardcoded rate in code, no silent 8.1 % fallback.
  // While loading we defer to 0 for the intermediate render only; the submit
  // handler blocks payment if the rate is not resolved yet or missing.
  const vatRate = storefrontVatRate ?? 0;
  const vatAmount = isRetry ? Number(retryOrder.vat_amount) : Math.max(0, (afterVolumeDiscount - codeDiscount - loyaltyDiscount)) * (vatRate / 100);
  const grandTotal = isRetry ? Number(retryOrder.total) : Math.max(0, (afterVolumeDiscount - codeDiscount - loyaltyDiscount + vatAmount));

  const handleToggleBump = (bump: SelectedBump) => {
    setSelectedBumps(prev => {
      const exists = prev.find(b => b.bumpId === bump.bumpId);
      if (exists) return prev.filter(b => b.bumpId !== bump.bumpId);
      return [...prev, bump];
    });
  };

  const handleUpdateBumpTarget = (bumpId: string, target: string) => {
    setSelectedBumps(prev => prev.map(b => b.bumpId === bumpId ? { ...b, target } : b));
  };

  const handleUpdateBumpVariation = (bump: SelectedBump) => {
    setSelectedBumps(prev => prev.map(b => b.bumpId === bump.bumpId ? bump : b));
  };

  const isFormValid = email && firstName && lastName && address && postalCode && city;

  // Create order and redirect to payment
  const handleCreateOrderAndPay = async () => {
    if (!isFormValid || isCreatingOrder) return;

    // P2.6 §5: block payment if VAT rate is not yet resolved from vat_settings
    // (either still loading or the row is missing/inactive for this
    // storefront). Never charge with a silent fallback rate.
    if (!isRetry) {
      if (vatLoading) {
        toast.error(t('checkout.vat_loading') || 'MwSt wird geladen, bitte kurz warten…');
        return;
      }
      if (vatError || storefrontVatRate == null) {
        toast.error(t('checkout.vat_missing') || 'MwSt-Satz für diesen Shop ist nicht konfiguriert.');
        return;
      }
      // P2.6 §4b: on non-CH the displayed/charged amounts depend on the FX
      // rate. Never submit while the rate is still loading or missing.
      if (sf.storefront !== 'ch') {
        if (cartDisplay.isLoading) {
          toast.error(t('checkout.fx_loading') || 'Wechselkurs wird geladen, bitte kurz warten…');
          return;
        }
        if (cartDisplay.isError || cartDisplay.sum == null) {
          toast.error(t('checkout.fx_missing') || 'Wechselkurs nicht verfügbar. Bitte später erneut versuchen.');
          return;
        }
      }
    }

    if (email && email.includes('@')) {
      trackContactIdentified({ email, firstName, lastName, country: countryCode, city, postalCode, address });
      trackCheckoutStep('email_captured', { email });
    }

    setIsCreatingOrder(true);
    try {
      let payOrderId = retryOrder?.id;
      let payOrderNumber = retryOrder?.order_number;

      if (!isRetry) {
        // Create the order - generate UUID client-side to avoid SELECT permission issues
        const clientOrderId = crypto.randomUUID();
        const amounts = buildOrderAmounts({
          storefront: detectStorefront(),
          subtotal,
          discount: volumeDiscount + codeDiscount,
          vat_amount: vatAmount,
          total: grandTotal,
          payment_fee: null,
          exchange_rate: sessionExchangeRate,
        });
        const { error: orderError } = await supabase
          .from('orders')
          .insert({
            id: clientOrderId,
            email,
            subtotal: amounts.subtotal,
            discount: amounts.discount,
            discount_code: discountApplied?.code || null,
            vat_amount: amounts.vat_amount,
            total: amounts.total,
            currency: amounts.currency,
            base_currency: amounts.base_currency,
            exchange_rate: amounts.exchange_rate,
            subtotal_base: amounts.subtotal_base,
            discount_base: amounts.discount_base,
            vat_amount_base: amounts.vat_amount_base,
            total_base: amounts.total_base,
            country_code: countryCode,
            status: 'pending',
            loyalty_points_redeemed: redeemPoints,
            loyalty_points_discount: loyaltyDiscount,
            notes: `${firstName} ${lastName}, ${address}, ${postalCode} ${city}${items.some(i => i.customInput) ? '\n\n' + items.filter(i => i.customInput).map(i => `[${i.productTitle}] ${i.customInput}`).join('\n') : ''}`,
            storefront: detectStorefront(),
            locale,
          });

        if (orderError) throw orderError;

        // Fetch SMM panel/service data for products in the cart
        const productIds = [...new Set(items.map(i => i.productId).filter(Boolean))];
        let productSmmMap: Record<string, { smm_panel_id: string | null; smm_service_id: string | null }> = {};
        let variationTypeMap: Record<string, { smm_panel_id: string | null; smm_service_id: string | null }> = {};
        if (productIds.length > 0) {
          const { data: productsData } = await supabase
            .from('products')
            .select('id, smm_panel_id, smm_service_id')
            .in('id', productIds);
          if (productsData) {
            productSmmMap = Object.fromEntries(productsData.map(p => [p.id, { smm_panel_id: p.smm_panel_id, smm_service_id: p.smm_service_id }]));
          }
          // Fetch variations with their product_type's SMM overrides
          const variationIds = items.map(i => i.variationId).filter(Boolean);
          if (variationIds.length > 0) {
            const { data: varsData } = await supabase
              .from('product_variations')
              .select('id, smm_panel_id, smm_service_id, product_type_id, product_types(smm_panel_id, smm_service_id)')
              .in('id', variationIds);
            if (varsData) {
              for (const v of varsData) {
                // Priority: variation-level > type-level
                const varSmm = (v.smm_panel_id || v.smm_service_id)
                  ? { smm_panel_id: v.smm_panel_id, smm_service_id: v.smm_service_id }
                  : null;
                const pt = v.product_types as any;
                const typeSmm = (pt?.smm_panel_id || pt?.smm_service_id)
                  ? { smm_panel_id: pt.smm_panel_id, smm_service_id: pt.smm_service_id }
                  : null;
                if (varSmm || typeSmm) {
                  variationTypeMap[v.id] = varSmm || typeSmm!;
                }
              }
            }
          }
        }

        // Insert order items
        const orderItems = items.flatMap(item => {
          const productSmm = item.productId ? productSmmMap[item.productId] : null;
          // Product type SMM overrides take priority over product-level SMM
          const typeSmm = item.variationId ? variationTypeMap[item.variationId] : null;
          const finalSmmPanelId = typeSmm?.smm_panel_id || productSmm?.smm_panel_id || null;
          const finalSmmServiceId = typeSmm?.smm_service_id || productSmm?.smm_service_id || null;
          const mainItem = {
            order_id: clientOrderId,
            product_id: item.productId || null,
            product_title: item.productTitle,
            variation_label: item.variationLabel,
            target: item.target,
            quantity: item.quantity,
            price: item.price,
            smm_panel_id: finalSmmPanelId,
            smm_service_id: finalSmmServiceId,
            product_type_label: item.typeLabel || null,
          };
          const result: any[] = [mainItem];
          if (item.giftId && item.giftLabel) {
            result.push({
              order_id: clientOrderId,
              product_id: item.productId || null,
              product_title: `Geschenk: ${item.giftLabel}`,
              variation_label: item.giftLabel,
              target: item.giftTarget || item.target,
              quantity: item.giftQuantity || 0,
              price: 0,
              is_gift: true,
              smm_panel_id: item.giftSmmPanelId || null,
              smm_service_id: item.giftSmmServiceId || null,
            });
          }
          return result;
        });

        // Add bump items to order
        for (const bump of selectedBumps) {
          orderItems.push({
            order_id: clientOrderId,
            product_id: bump.productId || null,
            product_title: `Bump: ${bump.productTitle}`,
            variation_label: bump.variationLabel,
            target: bump.target || items[0]?.target || '',
            quantity: bump.quantity,
            price: bump.finalPrice,
            smm_panel_id: null,
            smm_service_id: null,
          });
        }

        const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
        if (itemsError) throw itemsError;

        payOrderId = clientOrderId;
        payOrderNumber = 0; // order_number is auto-generated
      }

      if (selectedPayment === 'twint') {
        // TWINT: Create WooCommerce order and redirect
        const { data, error } = await supabase.functions.invoke('create-woo-order', {
          body: { orderId: payOrderId },
        });
        if (error) throw error;
        if (data?.url) {
          clearCart();
          window.location.href = data.url;
          return; // keep isCreatingOrder=true until page unloads
        } else {
          throw new Error('No payment URL received');
        }
      } else {
        // Stripe: handled by StripePaymentForm's onConfirm callback
        // This branch should not be reached for Stripe
        throw new Error('Stripe payments are handled inline');
      }
    } catch (err: any) {
      console.error('Error creating order/payment:', err?.message, err);
      toast.error(`Fehler: ${err?.message || 'Unbekannter Fehler'}`);
      setIsCreatingOrder(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    try {
      const { data, error } = await supabase.rpc('validate_discount_code', {
        _code: discountCode.trim().toUpperCase(),
        _storefront: detectStorefront(),
        _subtotal: subtotal,
        _email: email || null,
      });

      if (error || !data) {
        toast.error(t('checkout.code_error'));
        return;
      }

      const res = data as {
        valid: boolean;
        error?: string;
        code?: string;
        discount_percent?: number;
        discount_amount?: number;
        min_order_amount?: number;
        limit?: number;
      };

      if (!res.valid) {
        switch (res.error) {
          case 'expired':
            toast.error(t('checkout.code_expired'));
            break;
          case 'used_up':
            toast.error(t('checkout.code_used_up'));
            break;
          case 'min_order':
            toast.error(`${t('checkout.min_order')}: ${formatPrice(Number(res.min_order_amount || 0))}`);
            break;
          case 'email_required':
            toast.error(t('checkout.code_email_required'));
            break;
          case 'once_per_user':
            toast.error(t('checkout.code_once_per_user'));
            break;
          case 'per_user_limit':
            toast.error(t('checkout.code_per_user_limit').replace('{n}', String(res.limit || 1)));
            break;
          default:
            toast.error(t('checkout.invalid_code'));
        }
        return;
      }

      const percent = Number(res.discount_percent || 0);
      const amount = Number(res.discount_amount || 0);
      setDiscountApplied({ code: res.code!, percent, amount });
      const parts: string[] = [];
      if (percent > 0) parts.push(`${percent}%`);
      if (amount > 0) parts.push(formatPrice(amount));
      toast.success(`${parts.join(' + ')} ${t('checkout.discount_applied')}`);
    } catch {
      toast.error(t('checkout.code_error'));
    }
  };

  // Stripe onConfirm: creates order + PaymentIntent, returns clientSecret
  const handleStripeConfirm = async (): Promise<{ clientSecret: string; orderNumber?: number } | null> => {
    if (!isFormValid) {
      toast.error('Bitte fülle alle Rechnungsdaten aus.');
      return null;
    }

    if (email && email.includes('@')) {
      trackContactIdentified({ email, firstName, lastName, country: countryCode, city, postalCode, address });
      trackCheckoutStep('email_captured', { email });
    }

    try {
      let payOrderId = retryOrder?.id;

      if (!isRetry) {
        const clientOrderId = crypto.randomUUID();
        const amounts = buildOrderAmounts({
          storefront: detectStorefront(),
          subtotal,
          discount: volumeDiscount + codeDiscount,
          vat_amount: vatAmount,
          total: grandTotal,
          payment_fee: null,
          exchange_rate: sessionExchangeRate,
        });
        const { error: orderError } = await supabase
          .from('orders')
          .insert({
            id: clientOrderId,
            email,
            subtotal: amounts.subtotal,
            discount: amounts.discount,
            discount_code: discountApplied?.code || null,
            vat_amount: amounts.vat_amount,
            total: amounts.total,
            currency: amounts.currency,
            base_currency: amounts.base_currency,
            exchange_rate: amounts.exchange_rate,
            subtotal_base: amounts.subtotal_base,
            discount_base: amounts.discount_base,
            vat_amount_base: amounts.vat_amount_base,
            total_base: amounts.total_base,
            country_code: countryCode,
            status: 'pending',
            notes: `${firstName} ${lastName}, ${address}, ${postalCode} ${city}${items.some(i => i.customInput) ? '\n\n' + items.filter(i => i.customInput).map(i => `[${i.productTitle}] ${i.customInput}`).join('\n') : ''}`,
            storefront: detectStorefront(),
            locale,
          });

        if (orderError) throw orderError;

        const productIds = [...new Set(items.map(i => i.productId).filter(Boolean))];
        let productSmmMap: Record<string, { smm_panel_id: string | null; smm_service_id: string | null }> = {};
        let variationTypeMap: Record<string, { smm_panel_id: string | null; smm_service_id: string | null }> = {};
        if (productIds.length > 0) {
          const { data: productsData } = await supabase
            .from('products')
            .select('id, smm_panel_id, smm_service_id')
            .in('id', productIds);
          if (productsData) {
            productSmmMap = Object.fromEntries(productsData.map(p => [p.id, { smm_panel_id: p.smm_panel_id, smm_service_id: p.smm_service_id }]));
          }
          const variationIds = items.map(i => i.variationId).filter(Boolean);
          if (variationIds.length > 0) {
            const { data: varsData } = await supabase
              .from('product_variations')
              .select('id, smm_panel_id, smm_service_id, product_type_id, product_types(smm_panel_id, smm_service_id)')
              .in('id', variationIds);
            if (varsData) {
              for (const v of varsData) {
                const varSmm = (v.smm_panel_id || v.smm_service_id)
                  ? { smm_panel_id: v.smm_panel_id, smm_service_id: v.smm_service_id }
                  : null;
                const pt = v.product_types as any;
                const typeSmm = (pt?.smm_panel_id || pt?.smm_service_id)
                  ? { smm_panel_id: pt.smm_panel_id, smm_service_id: pt.smm_service_id }
                  : null;
                if (varSmm || typeSmm) {
                  variationTypeMap[v.id] = varSmm || typeSmm!;
                }
              }
            }
          }
        }

        const orderItems = items.flatMap(item => {
          const productSmm = item.productId ? productSmmMap[item.productId] : null;
          const typeSmm = item.variationId ? variationTypeMap[item.variationId] : null;
          const finalSmmPanelId = typeSmm?.smm_panel_id || productSmm?.smm_panel_id || null;
          const finalSmmServiceId = typeSmm?.smm_service_id || productSmm?.smm_service_id || null;
          const mainItem = {
            order_id: clientOrderId,
            product_id: item.productId || null,
            product_title: item.productTitle,
            variation_label: item.variationLabel,
            target: item.target,
            quantity: item.quantity,
            price: item.price,
            smm_panel_id: finalSmmPanelId,
            smm_service_id: finalSmmServiceId,
            product_type_label: item.typeLabel || null,
          };
          const result: any[] = [mainItem];
          if (item.giftId && item.giftLabel) {
            result.push({
              order_id: clientOrderId,
              product_id: item.productId || null,
              product_title: `Geschenk: ${item.giftLabel}`,
              variation_label: item.giftLabel,
              target: item.giftTarget || item.target,
              quantity: item.giftQuantity || 0,
              price: 0,
              is_gift: true,
              smm_panel_id: item.giftSmmPanelId || null,
              smm_service_id: item.giftSmmServiceId || null,
            });
          }
          return result;
        });

        // Add bump items to order (Stripe flow)
        for (const bump of selectedBumps) {
          orderItems.push({
            order_id: clientOrderId,
            product_id: bump.productId || null,
            product_title: `Bump: ${bump.productTitle}`,
            variation_label: bump.variationLabel,
            target: bump.target || items[0]?.target || '',
            quantity: bump.quantity,
            price: bump.finalPrice,
            smm_panel_id: null,
            smm_service_id: null,
          });
        }

        const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
        if (itemsError) throw itemsError;

        payOrderId = clientOrderId;
      }

      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: { orderId: payOrderId },
      });
      if (error) throw error;
      if (data?.clientSecret) {
        return { clientSecret: data.clientSecret, orderNumber: data.orderNumber };
      } else {
        throw new Error('No client secret received');
      }
    } catch (err: any) {
      console.error('Stripe order error:', err?.message, err);
      toast.error(`Fehler: ${err?.message || 'Unbekannter Fehler'}`);
      return null;
    }
  };

  // Clear cart on success
  useEffect(() => {
    if (isSuccess) {
      clearCart();
    }
  }, [isSuccess]);

  // Success page
  if (step === 2) {
    return (
      <CheckoutLayout step={2}>
        <div className="container max-w-lg py-24 text-center">
          <CheckCircle2 className="mx-auto mb-6 h-16 w-16 text-emerald-500" />
          <h1 className="mb-3 text-2xl font-bold">{t('checkout.order_received')}</h1>
          {orderNumber && (
            <p className="mb-2 font-mono text-lg font-semibold">{t('checkout.order_number')}: #{orderNumber}</p>
          )}
          <p className="mb-6 text-muted-foreground">
            {t('checkout.order_received_desc')}
          </p>
          <Link to={localePath('/')} className="text-primary hover:underline">{t('checkout.back_to_shop')}</Link>
        </div>
      </CheckoutLayout>
    );
  }

  // Show loading screen while order is being created & user is being redirected
  if (isCreatingOrder && items.length === 0) {
    return (
      <CheckoutLayout step={1}>
        <div className="container max-w-lg py-24 text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Weiterleitung zur Zahlung…</p>
        </div>
      </CheckoutLayout>
    );
  }
  if (items.length === 0 && !isSuccess && !isRetry && !retryLoading && !isCreatingOrder) {
    return (
      <CheckoutLayout step={0}>
        <div className="container max-w-lg py-24 text-center">
          <h1 className="mb-3 text-2xl font-bold">{t('checkout.cart_empty')}</h1>
          <Link to={localePath('/')} className="text-primary hover:underline">{t('checkout.back_to_shop')}</Link>
        </div>
      </CheckoutLayout>
    );
  }

  if (retryLoading) {
    return (
      <CheckoutLayout step={1}>
        <div className="container max-w-lg py-24 text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('checkout.loading_order')}</p>
        </div>
      </CheckoutLayout>
    );
  }

  const CardIcons = () => (
    <div className="flex items-center gap-1.5">
      <svg viewBox="0 0 38 24" className="h-6 w-auto" xmlns="http://www.w3.org/2000/svg"><rect width="38" height="24" rx="3" fill="#1A1F71"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold" fontFamily="system-ui">VISA</text></svg>
      <svg viewBox="0 0 38 24" className="h-6 w-auto" xmlns="http://www.w3.org/2000/svg"><rect width="38" height="24" rx="3" fill="#252525"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B"/><path d="M19 6.5a7 7 0 010 11 7 7 0 010-11z" fill="#FF5F00"/></svg>
      <svg viewBox="0 0 38 24" className="h-6 w-auto" xmlns="http://www.w3.org/2000/svg"><rect width="38" height="24" rx="3" fill="#FFB3C7"/><text x="19" y="15" textAnchor="middle" fill="#0A0B09" fontSize="7" fontWeight="bold" fontFamily="system-ui">Klarna</text></svg>
      <svg viewBox="0 0 38 24" className="h-6 w-auto" xmlns="http://www.w3.org/2000/svg"><rect width="38" height="24" rx="3" fill="#000"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold" fontFamily="system-ui"> Pay</text></svg>
      <svg viewBox="0 0 38 24" className="h-6 w-auto" xmlns="http://www.w3.org/2000/svg"><rect width="38" height="24" rx="3" fill="#003087"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold" fontFamily="system-ui">PayPal</text></svg>
    </div>
  );

  // P2.6 §3: Materialize the payment-method UI from the SSOT map
  // PAYMENT_METHODS_BY_STOREFRONT. CH still renders [TWINT, Stripe] in that
  // order; every other storefront renders Stripe only. Backend hard-lock in
  // `create-woo-order` remains as defense in depth.
  const paymentMethods = paymentMethodsForStorefront(detectStorefront()).map((id) => {
    if (id === 'twint') {
      return {
        id,
        label: 'TWINT',
        description: t('checkout.twint_desc'),
        logos: <img src={twintLogo} alt="TWINT" className="h-7 rounded" />,
      };
    }
    return {
      id,
      label: t('checkout.stripe_label'),
      description: '',
      logos: <CardIcons />,
    };
  });

  return (
    <CheckoutLayout step={1}>
      <div className="container px-4 py-6 md:py-8">

      <div className="grid gap-6 md:gap-8 lg:grid-cols-5">
        {/* Left: Form */}
        <div className="lg:col-span-3 order-2 lg:order-1">
          <h1 className="mb-2 text-xl md:text-2xl font-bold">
            {isRetry ? t('checkout.pay_order').replace('{number}', String(retryOrder?.order_number)) : t('checkout.complete_order')}
          </h1>
          <p className="mb-4 md:mb-6 text-sm text-muted-foreground">
            {isRetry ? t('checkout.pay_order_desc') : t('checkout.almost_done')}
          </p>

          {/* Billing details */}
          <div className="mb-6">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-foreground">{t('checkout.billing_details')}</h2>
            <div className="space-y-3">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => {
                    if (email && email.includes('@')) {
                      trackContactIdentified({ email, firstName, lastName, country: countryCode, city, postalCode, address });
                      trackCheckoutStep('email_captured', { email });
                      trackCheckoutField('email', 'completed', email);
                    }
                  }}
                  placeholder="deine@email.ch"
                  className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                {emailSuggestion && (
                  <p className="mt-1 text-xs text-amber-600">
                    {t('checkout.email_typo_prefix', 'Meintest du')}{' '}
                    <button
                      type="button"
                      onClick={() => setEmail(emailSuggestion)}
                      className="font-semibold underline hover:text-amber-700"
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('checkout.first_name')} *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('checkout.last_name')} *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('checkout.address')} *</label>
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onSelect={(details) => {
                    setAddress(details.address);
                    setPostalCode(details.postalCode);
                    setCity(details.city);
                    // Country is locked to the active storefront — do NOT
                    // overwrite it from address autocomplete.
                  }}
                  placeholder={t('checkout.address')}
                  className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('checkout.postal_code')} *</label>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={e => setPostalCode(e.target.value)}
                    className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('checkout.city')} *</label>
                  <input
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    className="w-full rounded-lg border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
              {/* Country is fixed by storefront and not shown in the UI. */}
              <input type="hidden" name="country_code" value={countryCode} />
            </div>
          </div>

          {/* Checkout Bumps */}
          {!isRetry && items.length > 0 && (
            <div className="mb-6">
              <CheckoutBumps
                cartProductIds={items.map(i => i.productId)}
                selectedBumps={selectedBumps}
                onToggleBump={handleToggleBump}
                onUpdateBumpTarget={handleUpdateBumpTarget}
                onUpdateBumpVariation={handleUpdateBumpVariation}
              />
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="mb-6">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-foreground">{t('checkout.payment')}</h2>
            <div className="space-y-2">
              {paymentMethods.map((pm) => (
                <button
                  key={pm.id}
                  onClick={() => setSelectedPayment(pm.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-all ${
                    selectedPayment === pm.id
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border bg-card hover:border-primary/40'
                  }`}
                >
                  <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                    selectedPayment === pm.id ? 'border-primary' : 'border-muted-foreground/40'
                  }`}>
                    {selectedPayment === pm.id && (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{pm.label}</span>
                      {pm.id === 'twint' && pm.logos}
                    </div>
                    {pm.id !== 'twint' && <div className="mt-1">{pm.logos}</div>}
                    {pm.description && <p className="mt-1 text-xs text-muted-foreground">{pm.description}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Stripe Elements Form or Pay Button */}
          {selectedPayment === 'stripe' ? (
            <div className="mb-6">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center rounded-xl border bg-card py-8 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('checkout.processing')}
                  </div>
                }
              >
                <StripePaymentForm
                  amount={grandTotal}
                  returnUrl={`${window.location.origin}/bestellung-erfolgreich`}
                  onConfirm={handleStripeConfirm}
                  onError={(msg) => {
                    toast.error(msg);
                  }}
                />
              </Suspense>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={handleCreateOrderAndPay}
                disabled={!isFormValid || isCreatingOrder}
                className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-cta py-3 text-sm font-bold text-cta-foreground shadow-[0_6px_18px_-6px_hsl(var(--cta)/0.5)] transition-all hover:bg-cta-hover hover:shadow-[0_8px_22px_-6px_hsl(var(--cta)/0.65)] active:scale-[0.99] disabled:opacity-50 disabled:shadow-none"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                {isCreatingOrder ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                <span className="relative">
                  {isCreatingOrder
                    ? t('checkout.creating_order')
                    : `${t('checkout.pay_now')} — ${fmt(grandTotal)}`}
                </span>
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <Lock className="h-3 w-3" />
                {t('checkout.ssl_secure', 'SSL-verschlüsselte Zahlung · 100% sicher')}
              </p>
            </div>
          )}

          {/* Money back guarantee */}
          <div className="mt-10 flex items-start gap-4 rounded-xl border bg-card p-5">
            <span className="text-3xl">🏅</span>
            <div>
              <h3 className="text-base font-bold">{t('checkout.money_back')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('checkout.money_back_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="lg:col-span-2 order-1 lg:order-2">
          {/* Trust badges */}
          <div className="mb-5 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-primary" />
              <span>{t('checkout.ssl_secure')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Headphones className="h-4 w-4 text-primary" />
              <span>{t('checkout.live_support')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-primary" />
              {/* CH keeps the existing translated label (byte-identical).
                  Non-CH derives from paymentMethodsForStorefront so AT never
                  advertises TWINT. */}
              <span>{sf.storefront === 'ch' ? t('checkout.payment_methods') : paymentMethodsLabel(sf.storefront)}</span>
            </div>
          </div>

          <div className="sticky top-24 rounded-xl border bg-card p-5">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider">{t('checkout.your_order')}</h3>

            <div className="mb-2 flex justify-between text-xs font-semibold text-muted-foreground">
              <span>{t('checkout.product')}</span>
              <span>{t('checkout.subtotal')}</span>
            </div>

            <div className="divide-y">
              {isRetry ? retryItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="font-medium">{item.product_title}</p>
                      <p className="text-xs text-muted-foreground">{item.variation_label}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">× {item.quantity}</span>
                  </div>
                  <span className="font-mono font-semibold">{formatPrice(Number(item.price))}</span>
                </div>
              )) : items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 text-sm">
                  <div className="flex items-center gap-2">
                    {item.productImage && (
                      <img src={item.productImage} alt={item.productTitle} className="h-9 w-9 shrink-0 rounded object-contain" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{item.productTitle}</p>
                      <p className="text-xs text-muted-foreground">{item.variationLabel}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">× 1</span>
                  </div>
                  <span className="ml-2 font-mono font-semibold">{fmt(lineDisplayPrices[idx] ?? item.price)}</span>
                </div>
              ))}
              {/* Show selected bumps in summary */}
              {selectedBumps.map(bump => (
                <div key={bump.bumpId} className="flex items-center justify-between py-3 text-sm">
                  <div className="flex items-center gap-2">
                    {bump.productImage && (
                      <img src={bump.productImage} alt={bump.productTitle} className="h-9 w-9 shrink-0 rounded object-contain" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{bump.productTitle}</p>
                      <p className="text-xs text-muted-foreground">{bump.variationLabel}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">× 1</span>
                  </div>
                  <span className="ml-2 font-mono font-semibold">{fmt(bumpLineDisplay(bump.finalPrice))}</span>
                </div>
              ))}
            </div>

            <div className="mt-3 space-y-2 border-t pt-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('checkout.subtotal')}</span>
                <span className="font-mono">{fmt(subtotal)}</span>
              </div>

              {hasVolumeDiscount && (
                <div className="flex justify-between text-sm text-[hsl(var(--success))]">
                  <span>{discountPercent}% {t('checkout.discount')}</span>
                  <span className="font-mono">-{fmt(volumeDiscount)}</span>
                </div>
              )}

              {discountApplied && codeDiscount > 0 && (
                <div className="flex justify-between text-sm text-[hsl(var(--success))]">
                  <span>
                    {discountApplied.percent > 0 && `${discountApplied.percent}%`}
                    {discountApplied.percent > 0 && discountApplied.amount > 0 && ' + '}
                    {discountApplied.amount > 0 && fmt(discountApplied.amount)}
                    {' '}{t('checkout.code_discount')}
                  </span>
                  <span className="font-mono">-{fmt(codeDiscount)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('checkout.vat')} {vatRate}%</span>
                <span className="font-mono">{fmt(vatAmount)}</span>
              </div>

              {loyaltyDiscount > 0 && (
                <div className="flex justify-between text-sm text-[hsl(var(--success))]">
                  <span>{t('loyalty.row_label').replace('{points}', redeemPoints.toLocaleString('de-CH'))}</span>
                  <span className="font-mono">-{fmt(loyaltyDiscount)}</span>
                </div>
              )}

              <div className="flex justify-between border-t pt-2 text-base font-bold">
                <span>{t('checkout.total')}</span>
                <span className="font-mono">{fmt(grandTotal)}</span>
              </div>
              <PointsBadge amount={Math.max(0, grandTotal)} size="md" />
            </div>

            {!isRetry && (
              <div className="mt-4 flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value)}
                  placeholder={t('checkout.discount_code')}
                  className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <button
                  onClick={handleApplyDiscount}
                  className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"
                >
                  {t('checkout.use_code').split(' ').join('\n')}
                </button>
              </div>
            )}

            {!isRetry && user && (
              <div className="mt-4">
                <LoyaltyRedeemBox
                  redeemPoints={redeemPoints}
                  onChange={setRedeemPoints}
                  maxDiscountChf={Math.max(0, afterVolumeDiscount - codeDiscount)}
                />
              </div>
            )}

            <p className="mt-4 text-xs text-primary">
              {t('checkout.privacy_note')}
            </p>
          </div>
        </div>
        </div>
      </div>
    </CheckoutLayout>
  );
}
